
import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, Mail, MessageSquare, Calendar, DollarSign, GraduationCap, AlertCircle } from "lucide-react";

interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  enrollmentNotifications: boolean;
  paymentNotifications: boolean;
  classReminders: boolean;
}
interface Props {
  notificationSettings: NotificationSettings;
  onNotificationToggle: (field: keyof NotificationSettings) => void;
  isSaving: boolean;
}
const ParentPortalNotificationsTab: React.FC<Props> = ({
  notificationSettings,
  onNotificationToggle,
  isSaving,
}) => {
  const deliveryMethods = [
    {
      key: 'emailEnabled' as keyof NotificationSettings,
      icon: Mail,
      title: 'Email Notifications',
      description: 'Send notifications via email to parents',
      checked: notificationSettings.emailEnabled,
      category: 'delivery'
    },
    {
      key: 'smsEnabled' as keyof NotificationSettings,
      icon: MessageSquare,
      title: 'SMS Notifications',
      description: 'Send text message notifications to parents (requires SMS service)',
      checked: notificationSettings.smsEnabled,
      category: 'delivery'
    }
  ];

  const notificationTypes = [
    {
      key: 'enrollmentNotifications' as keyof NotificationSettings,
      icon: GraduationCap,
      title: 'Class Enrollment Notifications',
      description: 'Notify parents when students are enrolled, waitlisted, or removed from classes',
      checked: notificationSettings.enrollmentNotifications,
      category: 'content'
    },
    {
      key: 'paymentNotifications' as keyof NotificationSettings,
      icon: DollarSign,
      title: 'Payment & Invoice Notifications',
      description: 'Send notifications for new invoices, payment confirmations, and overdue balances',
      checked: notificationSettings.paymentNotifications,
      category: 'content'
    },
    {
      key: 'classReminders' as keyof NotificationSettings,
      icon: Calendar,
      title: 'Class Reminders',
      description: 'Send automatic reminders before scheduled classes and activities',
      checked: notificationSettings.classReminders,
      category: 'content'
    }
  ];

  return (
    <div className="max-w-5xl space-y-6">
      {/* Delivery Methods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Notification Delivery Methods
          </CardTitle>
          <CardDescription>
            Configure how notifications are delivered to parents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {deliveryMethods.map((method) => {
              const IconComponent = method.icon;
              return (
                <div
                  key={method.key}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      <IconComponent className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label htmlFor={method.key} className="font-medium cursor-pointer">
                        {method.title}
                      </Label>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {method.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={method.key}
                    checked={method.checked}
                    onCheckedChange={() => onNotificationToggle(method.key)}
                    disabled={isSaving}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Notification Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Types
          </CardTitle>
          <CardDescription>
            Choose which types of notifications parents will receive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {notificationTypes.map((notification) => {
              const IconComponent = notification.icon;
              return (
                <div
                  key={notification.key}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      <IconComponent className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label htmlFor={notification.key} className="font-medium cursor-pointer">
                        {notification.title}
                      </Label>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {notification.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={notification.key}
                    checked={notification.checked}
                    onCheckedChange={() => onNotificationToggle(notification.key)}
                    disabled={isSaving}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Configuration Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Notification Configuration</h4>
              <p className="text-sm text-blue-700 mt-1">
                SMS notifications require additional setup with a messaging service provider. 
                Email notifications are ready to use and will be sent from your organization's configured email address.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentPortalNotificationsTab;
